import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Lock, ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        setError("Identifiant ou mot de passe incorrect");
        toast.error("Identifiant ou mot de passe incorrect");
        setLoading(false);
        return;
      }

      const data = await res.json();
      localStorage.setItem("label_energie_auth", "true");
      localStorage.setItem("label_energie_user", data.username);
      localStorage.setItem("label_energie_role", data.role);
      if (data.commissions) {
        localStorage.setItem("label_energie_commissions", JSON.stringify(data.commissions));
      }
      if (data.payment_delays) {
        localStorage.setItem("label_energie_payment_delays", JSON.stringify(data.payment_delays));
      }
      toast.success("Bienvenue !");
      if (data.role === "telepro") {
        if (data.commissions) {
          navigate("/dashboard-regie");
        } else {
          navigate("/leads");
        }
      } else if (data.role === "regie") {
        navigate("/dashboard-regie");
      } else if (data.role === "administratif") {
        navigate("/");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
      toast.error("Erreur de connexion");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Logo Centered */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop')`
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[hsl(200,28%,20%)]/80 backdrop-blur-sm" />
        
        {/* Logo Centered */}
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-[hsl(145,100%,45%)] flex items-center justify-center shadow-2xl">
              <Lightbulb className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-extrabold text-5xl text-white tracking-tight">
                RENOVA
              </h1>
              <p className="text-xl text-[hsl(187,71%,59%)] font-semibold tracking-widest">
                SOLUTION
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[hsl(145,100%,45%)] flex items-center justify-center">
                <Lightbulb className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="font-heading font-bold text-2xl text-foreground">
                  RENOVA
                </h1>
                <p className="text-xs text-[hsl(187,71%,59%)] font-semibold tracking-widest">
                  SOLUTION
                </p>
              </div>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground">
              Bon retour !
            </h2>
            <p className="text-muted-foreground mt-1">
              Connectez-vous pour accéder à votre espace
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Identifiant
                </label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError("");
                  }}
                  placeholder="Entrez votre identifiant"
                  className={`rounded-xl h-12 bg-muted/50 border-border focus:ring-2 focus:ring-[hsl(187,71%,59%)]/50 focus:border-[hsl(187,71%,59%)] transition-all ${error ? 'border-destructive' : ''}`}
                  data-testid="username-input"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  Mot de passe
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Entrez votre mot de passe"
                  className={`rounded-xl h-12 bg-muted/50 border-border focus:ring-2 focus:ring-[hsl(187,71%,59%)]/50 focus:border-[hsl(187,71%,59%)] transition-all ${error ? 'border-destructive' : ''}`}
                  data-testid="password-input"
                />
              </div>

              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                  <p className="text-sm text-destructive text-center">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !username || !password}
                className="w-full h-12 rounded-xl bg-[hsl(200,28%,33%)] hover:bg-[hsl(200,30%,24%)] text-white font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
                data-testid="login-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Cryptodigger &copy; 2026
          </p>
        </div>
      </div>
    </div>
  );
}

export const isAuthenticated = () => {
  return localStorage.getItem("label_energie_auth") === "true";
};

export const getUserRole = () => {
  return localStorage.getItem("label_energie_role") || "admin_full";
};

export const getCurrentUser = () => {
  return localStorage.getItem("label_energie_user") || "";
};

export const isRestrictedUser = () => {
  const role = getUserRole();
  return role === "admin_restricted" || role === "administratif";
};

export const isTelepro = () => {
  return getUserRole() === "telepro";
};

export const isRegie = () => {
  return getUserRole() === "regie";
};

export const isAdmin = () => {
  const role = getUserRole();
  return role === "admin_full" || role === "admin_restricted" || role === "administratif";
};

export const isAdministratif = () => {
  return getUserRole() === "administratif";
};

export const getUserCommissions = () => {
  try {
    const raw = localStorage.getItem("label_energie_commissions");
    return raw ? JSON.parse(raw) : null;
  } catch (err) { return null; }
};

export const getUserPaymentDelays = () => {
  try {
    const raw = localStorage.getItem("label_energie_payment_delays");
    return raw ? JSON.parse(raw) : null;
  } catch (err) { return null; }
};

export const logout = () => {
  localStorage.removeItem("label_energie_auth");
  localStorage.removeItem("label_energie_user");
  localStorage.removeItem("label_energie_role");
  localStorage.removeItem("label_energie_commissions");
  localStorage.removeItem("label_energie_payment_delays");
};
